// Thin wrapper around the shared `apiFetch` helper, hard-coding the
// `app: 'interior'` flag so each call lands on the interior sub-app
// (`VITE_INTERIOR_API_BASE`, which CI sets to
// `${LIGHTNING_API_BASE}/api/interior`).
//
// Mirrors `web/src/video/api.ts` in spirit but exposes the
// lightning-style /scans, /api/jobs/*, /api/batches/*, /api/settings/*
// surface implemented in `src/interior/server.py`.

import { apiFetch, INTERIOR_API_BASE } from '../utils/api';
import { applySearchParams, type HistorySearch } from '../components/historyQuery';
import type {
  BatchCreatedResponse,
  BatchHistoryResponse,
  BatchSummary,
  CreateScanResponse,
  DateRange,
  GenerationLogResponse,
  HistoryResponse,
  InteriorBackground,
  InteriorConfig,
  InteriorConfigYaml,
  InteriorDebugCropsResponse,
  InteriorDebugMetadataResponse,
  InteriorDetectionsResponse,
  InteriorGenerationDebugResponse,
  InteriorHealth,
  InteriorScanSettings,
  Job,
  JobImages,
  PublishResponse,
  ReviewScene,
  ScanReview,
} from './types';

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // body wasn't JSON; keep statusText
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

async function emptyOrThrow(res: Response): Promise<void> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
}

// ----- /scans -------------------------------------------------------------

export async function createScan(
  scanId: string,
  opts?: { callbackUrl?: string; source?: string; backgroundName?: string; settings?: InteriorScanSettings },
): Promise<CreateScanResponse> {
  const body: Record<string, unknown> = {
    scan_id: scanId,
    source: opts?.source ?? 'manual',
  };
  if (opts?.callbackUrl) body.callback_url = opts.callbackUrl;
  // `undefined` is omitted (server default); the `'none'` sentinel is a
  // valid, meaningful value (skip background replacement) so it's sent.
  if (opts?.backgroundName !== undefined) body.background_name = opts.backgroundName;
  // The web UI always sends the full settings object so the per-request
  // values reliably override the server config.
  if (opts?.settings !== undefined) body.settings = opts.settings;
  const res = await apiFetch('/scans', {
    app: 'interior',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<CreateScanResponse>(res);
}

// ----- /api/jobs/* --------------------------------------------------------

export async function listLiveJobs(): Promise<Job[]> {
  const res = await apiFetch('/api/jobs/live', { app: 'interior' });
  return jsonOrThrow<Job[]>(res);
}

export async function listHistoryJobs(
  dateRange: DateRange,
  status: string | null,
  page: number,
  pageSize: number,
  scanId?: string | null,
  organizationId?: string | null,
  search?: HistorySearch,
): Promise<HistoryResponse> {
  const params = new URLSearchParams();
  if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
  if (dateRange.to) params.set('to_date', dateRange.to.toISOString());
  if (status) params.set('status', status);
  if (scanId) params.set('scan_id', scanId);
  if (organizationId) params.set('organization_id', organizationId);
  applySearchParams(params, search);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiFetch(`/api/jobs/history?${params.toString()}`, {
    app: 'interior',
  });
  return jsonOrThrow<HistoryResponse>(res);
}

/**
 * Bulk download of historical interior jobs (CSV or JSON). Same UX as
 * lightning's export button — triggers a browser download.
 */
export async function exportJobs(
  dateRange: DateRange,
  status: string | null,
  format: 'csv' | 'json',
  search?: HistorySearch,
): Promise<void> {
  const params = new URLSearchParams();
  if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
  if (dateRange.to) params.set('to_date', dateRange.to.toISOString());
  if (status) params.set('status', status);
  applySearchParams(params, search);
  params.set('format', format);
  const res = await apiFetch(`/api/jobs/export?${params.toString()}`, {
    app: 'interior',
  });
  if (!res.ok) {
    throw new Error(`Failed to export: ${res.status} ${res.statusText}`);
  }
  const contentDisposition = res.headers.get('Content-Disposition');
  let filename = `interior_jobs_export.${format}`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename=(.+)/);
    if (match) filename = match[1];
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function getJob(jobId: string): Promise<Job> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}`, { app: 'interior' });
  return jsonOrThrow<Job>(res);
}

export async function rerunJob(jobId: string): Promise<CreateScanResponse> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/rerun`, {
    app: 'interior',
    method: 'POST',
  });
  return jsonOrThrow<CreateScanResponse>(res);
}

export async function abortJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/abort`, {
    app: 'interior',
    method: 'POST',
  });
  return emptyOrThrow(res);
}

export async function deleteJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}`, {
    app: 'interior',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

export async function deleteHistoryJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/history/${encodeURIComponent(jobId)}`, {
    app: 'interior',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

export async function abortAllLive(): Promise<void> {
  const res = await apiFetch('/api/jobs/live', {
    app: 'interior',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

export async function fetchScanImages(jobId: string): Promise<JobImages> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/images`, { app: 'interior' });
  return jsonOrThrow<JobImages>(res);
}

/**
 * Fetch a full 360 panorama (original|enhanced) as an authenticated blob
 * and return an object URL. The /images endpoint returns presigned S3
 * URLs that a plain <img> can display but a WebGL texture can't read
 * (cross-origin, no CORS). This same-origin proxy goes through apiFetch
 * (so the auth header is attached) and yields a blob: URL the panorama
 * viewer can fetch freely. Callers must URL.revokeObjectURL when done.
 */
export async function fetchFullImageObjectUrl(
  jobId: string,
  variant: 'original' | 'enhanced',
): Promise<string> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/full-image/${variant}`,
    { app: 'interior' },
  );
  if (!res.ok) {
    throw new Error(`Failed to load full image: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ----- debug panel --------------------------------------------------------
// Interior mirror of lightning's ScanDebugModal fetchers.

/** Per-scene original/enhanced crops + enhancement prompt. */
export async function fetchInteriorDebugCrops(
  jobId: string,
): Promise<InteriorDebugCropsResponse> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/debug/crops`,
    { app: 'interior' },
  );
  return jsonOrThrow<InteriorDebugCropsResponse>(res);
}

/** Exact ordered input/output images and request data for every generation attempt. */
export async function fetchInteriorGenerationDebug(
  jobId: string,
): Promise<InteriorGenerationDebugResponse> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/debug/generation-calls`,
    { app: 'interior' },
  );
  return jsonOrThrow<InteriorGenerationDebugResponse>(res);
}

/** Parts detections (labels + scores + boxes) + panorama dimensions. */
export async function fetchInteriorDetections(
  jobId: string,
): Promise<InteriorDetectionsResponse> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/debug/detections`,
    { app: 'interior' },
  );
  return jsonOrThrow<InteriorDetectionsResponse>(res);
}

/**
 * Fetch the downscaled detections backdrop as an authenticated blob and
 * return an object URL (same pattern as {@link fetchFullImageObjectUrl}).
 * The caller owns the URL and must `URL.revokeObjectURL` it when done.
 */
export async function fetchInteriorDetectionsImageObjectUrl(
  jobId: string,
): Promise<string> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/debug/detections-image`,
    { app: 'interior' },
  );
  if (!res.ok) {
    throw new Error(`Failed to load detections image: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Request params (`task_params`) + resolved vehicle metadata. */
export async function fetchInteriorDebugMetadata(
  jobId: string,
): Promise<InteriorDebugMetadataResponse> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/debug/metadata`,
    { app: 'interior' },
  );
  return jsonOrThrow<InteriorDebugMetadataResponse>(res);
}

/** Parsed `generation_log.csv` (columns + rows). */
export async function fetchInteriorGenerationLog(
  jobId: string,
): Promise<GenerationLogResponse> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/debug/generation-log`,
    { app: 'interior' },
  );
  return jsonOrThrow<GenerationLogResponse>(res);
}

// ----- /api/batches/* -----------------------------------------------------

export async function submitBatch(
  filename: string,
  scanIds: string[],
  opts?: { callbackUrl?: string; backgroundName?: string; settings?: InteriorScanSettings },
): Promise<BatchCreatedResponse> {
  const body: Record<string, unknown> = {
    filename,
    scan_ids: scanIds,
  };
  if (opts?.callbackUrl) body.callback_url = opts.callbackUrl;
  if (opts?.backgroundName !== undefined) body.background_name = opts.backgroundName;
  if (opts?.settings !== undefined) body.settings = opts.settings;
  const res = await apiFetch('/api/batches', {
    app: 'interior',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<BatchCreatedResponse>(res);
}

export async function listActiveBatches(): Promise<BatchSummary[]> {
  const res = await apiFetch('/api/batches', { app: 'interior' });
  return jsonOrThrow<BatchSummary[]>(res);
}

export async function listBatchHistory(
  dateRange: DateRange,
  page: number,
  pageSize: number,
): Promise<BatchHistoryResponse> {
  const params = new URLSearchParams();
  if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
  if (dateRange.to) params.set('to_date', dateRange.to.toISOString());
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiFetch(`/api/batches/history?${params.toString()}`, {
    app: 'interior',
  });
  return jsonOrThrow<BatchHistoryResponse>(res);
}

export async function listBatchJobs(batchId: string): Promise<Job[]> {
  const res = await apiFetch(`/api/batches/${encodeURIComponent(batchId)}/jobs`, { app: 'interior' });
  return jsonOrThrow<Job[]>(res);
}

export async function deleteBatch(batchId: string): Promise<void> {
  const res = await apiFetch(`/api/batches/${encodeURIComponent(batchId)}`, {
    app: 'interior',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

// ----- /api/settings/* + /health -----------------------------------------

export async function fetchInteriorHealth(): Promise<InteriorHealth> {
  const res = await apiFetch('/health', { app: 'interior' });
  return jsonOrThrow<InteriorHealth>(res);
}

export async function fetchConfigYaml(): Promise<InteriorConfigYaml> {
  const res = await apiFetch('/api/settings/config-yaml', { app: 'interior' });
  return jsonOrThrow<InteriorConfigYaml>(res);
}

/**
 * Lightweight runtime config the SPA reads on load to gate the Manual
 * Review tab. Returns just the deployment's `generation_callback_mode`
 * (unauthenticated, mirrors `/health`) instead of the heavy
 * `config-yaml` payload.
 */
export async function fetchInteriorConfig(): Promise<InteriorConfig> {
  const res = await apiFetch('/api/interior-config', { app: 'interior' });
  return jsonOrThrow<InteriorConfig>(res);
}

// ----- /api/settings/backgrounds -----------------------------------------
// Interior-scoped mirror of lightning's photobooth management (no prompt
// metadata). The list always includes the synthetic `'none'` entry for
// the interior-only "No background" option.

export async function listBackgrounds(): Promise<InteriorBackground[]> {
  const res = await apiFetch('/api/settings/backgrounds', { app: 'interior' });
  return jsonOrThrow<InteriorBackground[]>(res);
}

export async function uploadBackground(name: string, file: File): Promise<InteriorBackground> {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('file', file);
  const res = await apiFetch('/api/settings/backgrounds', {
    app: 'interior',
    method: 'POST',
    body: formData,
  });
  return jsonOrThrow<InteriorBackground>(res);
}

export async function deleteBackground(name: string): Promise<void> {
  const res = await apiFetch(`/api/settings/backgrounds/${encodeURIComponent(name)}`, {
    app: 'interior',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

export async function setDefaultBackground(name: string): Promise<InteriorBackground> {
  const res = await apiFetch('/api/settings/backgrounds/default', {
    app: 'interior',
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  return jsonOrThrow<InteriorBackground>(res);
}

/**
 * Fetch a background image thumbnail as an object URL.
 *
 * Goes through `apiFetch` so the request carries the
 * `Authorization: Bearer <token>` header the deployment's API gateway
 * enforces — a raw `<img src>` to the protected route can't attach it
 * and is rejected (401), which renders as a broken image. The caller
 * owns the returned URL and must `URL.revokeObjectURL()` it when done.
 * Mirrors the authenticated blob loading used elsewhere (e.g. the
 * interior `ImagesModal`).
 */
export async function fetchBackgroundImageObjectUrl(name: string): Promise<string> {
  const res = await apiFetch(
    `/api/settings/backgrounds/${encodeURIComponent(name)}/image`,
    { app: 'interior' },
  );
  if (!res.ok) {
    throw new Error(`Failed to load background image: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ----- Manual verification ------------------------------------------------
// Review-queue table + per-scan review editor backing the
// `/interior/manual-verification` screens. Mirrors the lightning-style
// imperative pattern used above (call -> caller reloads); per-scene action
// endpoints each return the SINGLE updated scene object so the review page
// can patch just that card without a full refetch.

/**
 * Paginated Manual Verification queue (jobs held in `generated` /
 * `published`). Reuses the `HistoryResponse` envelope + the same
 * date/scan/org/status filters as {@link listHistoryJobs}.
 */
export async function listManualVerification(
  dateRange: DateRange,
  status: string | null,
  page: number,
  pageSize: number,
  scanId?: string | null,
  organizationId?: string | null,
): Promise<HistoryResponse> {
  const params = new URLSearchParams();
  if (dateRange.from) params.set('from_date', dateRange.from.toISOString());
  if (dateRange.to) params.set('to_date', dateRange.to.toISOString());
  if (status) params.set('status', status);
  if (scanId) params.set('scan_id', scanId);
  if (organizationId) params.set('organization_id', organizationId);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  const res = await apiFetch(`/api/manual-verification?${params.toString()}`, {
    app: 'interior',
  });
  return jsonOrThrow<HistoryResponse>(res);
}

/** Full per-scan review payload (crops + per-scene review state). */
export async function fetchScanReview(jobId: string): Promise<ScanReview> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/review`, {
    app: 'interior',
  });
  return jsonOrThrow<ScanReview>(res);
}

// All five per-scene actions hit `/api/jobs/{job}/scenes/{scene}/<action>`
// and return the single updated scene object. On failure the backend
// replies 4xx/5xx with `{detail}`, which `jsonOrThrow` surfaces as the
// thrown Error's message for inline per-card display.
async function sceneAction(
  jobId: string,
  scene: string,
  action: 'approve' | 'regenerate' | 'undo' | 'redo',
): Promise<ReviewScene> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/scenes/${encodeURIComponent(scene)}/${action}`,
    { app: 'interior', method: 'POST' },
  );
  return jsonOrThrow<ReviewScene>(res);
}

export function approveScene(jobId: string, scene: string): Promise<ReviewScene> {
  return sceneAction(jobId, scene, 'approve');
}

export function regenerateScene(jobId: string, scene: string): Promise<ReviewScene> {
  return sceneAction(jobId, scene, 'regenerate');
}

export function undoScene(jobId: string, scene: string): Promise<ReviewScene> {
  return sceneAction(jobId, scene, 'undo');
}

export function redoScene(jobId: string, scene: string): Promise<ReviewScene> {
  return sceneAction(jobId, scene, 'redo');
}

/** Re-prompt the current crop with a free-text instruction. */
export async function promptScene(
  jobId: string,
  scene: string,
  prompt: string,
): Promise<ReviewScene> {
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/scenes/${encodeURIComponent(scene)}/prompt`,
    {
      app: 'interior',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt }),
    },
  );
  return jsonOrThrow<ReviewScene>(res);
}

/**
 * Publish the approved crops: reconstructs the callback result with only
 * approved (edited) scenes, marks the job `published`, and fires the
 * callback. Returns the count of published images.
 */
export async function publishScan(jobId: string): Promise<PublishResponse> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/publish`, {
    app: 'interior',
    method: 'POST',
  });
  return jsonOrThrow<PublishResponse>(res);
}

// INTERIOR_API_BASE is re-exported so tests + dev tools can compose the
// same URL the SPA would; runtime code should use the helpers above.
export { INTERIOR_API_BASE };
